import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Download, Smartphone, Share2, Chrome, MoreVertical, CheckCircle2, ArrowLeft, Package, Shield, Settings, FileDown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import { APP_CONFIG } from "@/lib/config";

// Configuración del APK - sincronizada con config.ts
const APK_CONFIG = {
  fileName: "Athenea.apk",
  version: APP_CONFIG.version,
  size: "~6 MB",
  downloadPath: "/downloads/Athenea.apk"
};

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export default function InstallApp() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Detectar si ya está instalada
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Capturar el evento de instalación
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      setIsInstalled(true);
    }
  };

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 p-3 sm:p-6">
      <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6">
        {/* Header */}
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate(-1)}
          className="mb-2 sm:mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {language === "es" ? "Volver" : "Back"}
        </Button>

        <div className="text-center space-y-2 sm:space-y-3">
          <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto bg-primary/10 rounded-full flex items-center justify-center animate-in fade-in scale-in duration-500">
            <Download className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
          </div>
          <h1 className="text-2xl sm:text-4xl font-bold px-4 animate-in fade-in slide-in-from-bottom-2 duration-500 delay-150">
            {language === "es" ? "📱 Instalar App" : "📱 Install App"}
          </h1>
          <p className="text-muted-foreground text-sm sm:text-lg max-w-md mx-auto px-4 leading-relaxed animate-in fade-in slide-in-from-bottom-2 duration-500 delay-300">
            {language === "es" 
              ? "Instala Magma MFG en tu móvil para acceso rápido y funcionalidad offline"
              : "Install Magma MFG on your mobile for quick access and offline functionality"}
          </p>
        </div>

        {/* Descarga APK Nativo para Android */}
        <Card className="p-4 sm:p-6 space-y-4 border-2 border-orange-500/30 bg-gradient-to-br from-orange-500/10 to-red-500/5 animate-in fade-in slide-in-from-top-4 duration-500">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                <Package className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="font-bold text-lg sm:text-xl">
                  {language === "es" ? "📦 App Nativa Android" : "📦 Native Android App"}
                </h2>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {language === "es"
                    ? `Versión ${APK_CONFIG.version} • ${APK_CONFIG.size}`
                    : `Version ${APK_CONFIG.version} • ${APK_CONFIG.size}`}
                </p>
              </div>
            </div>

            <div className="bg-orange-500/10 rounded-lg p-3 text-sm">
              <p className="text-orange-700 dark:text-orange-300">
                {language === "es"
                  ? "⚡ La app nativa incluye soporte NFC completo y mejor rendimiento"
                  : "⚡ Native app includes full NFC support and better performance"}
              </p>
            </div>

            <Button
              asChild
              className="w-full min-h-[52px] bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold shadow-lg"
              size="lg"
            >
              <a href={APK_CONFIG.downloadPath} download={APK_CONFIG.fileName}>
                <Download className="w-5 h-5 mr-2" />
                {language === "es" ? "Descargar APK" : "Download APK"}
              </a>
            </Button>

            {/* Instrucciones de instalación APK */}
            <div className="space-y-3 pt-2 border-t border-orange-500/20">
              <p className="text-sm font-medium text-center">
                {language === "es" ? "Pasos para instalar:" : "Installation steps:"}
              </p>
              <div className="space-y-2">
                {[
                  {
                    icon: <FileDown className="w-4 h-4 text-orange-500" />,
                    text: language === "es"
                      ? "1. Descarga el APK tocando el botón"
                      : "1. Download the APK by tapping the button"
                  },
                  {
                    icon: <Shield className="w-4 h-4 text-orange-500" />,
                    text: language === "es"
                      ? "2. Si pide permiso, activa 'Instalar apps desconocidas'"
                      : "2. If prompted, enable 'Install unknown apps'"
                  },
                  {
                    icon: <Settings className="w-4 h-4 text-orange-500" />,
                    text: language === "es"
                      ? "3. Abre el archivo descargado desde notificaciones"
                      : "3. Open the downloaded file from notifications"
                  },
                  {
                    icon: <CheckCircle2 className="w-4 h-4 text-orange-500" />,
                    text: language === "es"
                      ? "4. Toca 'Instalar' y espera a que termine"
                      : "4. Tap 'Install' and wait for completion"
                  }
                ].map((step, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-xs text-muted-foreground">
                    {step.icon}
                    <span>{step.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>

        {isInstalled ? (
          <Card className="p-6 sm:p-8 text-center space-y-3 sm:space-y-4 border-2 border-green-500/20 bg-green-500/5 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto bg-green-500/20 rounded-full flex items-center justify-center animate-in scale-in duration-700 delay-150">
              <CheckCircle2 className="w-8 h-8 sm:w-10 sm:h-10 text-green-500" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-green-500 animate-in fade-in slide-in-from-bottom-2 duration-500 delay-300">
              {language === "es" ? "¡App Instalada! ✅" : "App Installed! ✅"}
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed animate-in fade-in duration-500 delay-450">
              {language === "es"
                ? "La aplicación ya está instalada en tu dispositivo. Búscala en tu pantalla de inicio."
                : "The app is already installed on your device. Look for it on your home screen."}
            </p>
            <Button onClick={() => navigate("/")} size="lg" className="min-h-[44px] animate-in fade-in slide-in-from-bottom-2 duration-500 delay-600">
              {language === "es" ? "Ir al Dashboard" : "Go to Dashboard"}
            </Button>
          </Card>
        ) : (
          <>
            {/* Botón de instalación rápida para Android/Desktop */}
            {deferredPrompt && (
              <Card className="p-4 sm:p-6 space-y-3 sm:space-y-4 border-2 border-primary bg-primary/5 animate-in fade-in slide-in-from-top-4 duration-500">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <Smartphone className="w-5 h-5 sm:w-6 sm:h-6 text-primary-foreground" />
                  </div>
                  <div>
                    <h2 className="font-bold text-base sm:text-lg">
                      {language === "es" ? "🚀 Instalación Rápida" : "🚀 Quick Install"}
                    </h2>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {language === "es"
                        ? "¡Tu navegador soporta instalación automática!"
                        : "Your browser supports automatic installation!"}
                    </p>
                  </div>
                </div>
                <Button onClick={handleInstallClick} className="w-full min-h-[52px]" size="lg">
                  <Download className="w-5 h-5 mr-2" />
                  {language === "es" ? "Instalar Ahora" : "Install Now"}
                </Button>
              </Card>
            )}

            {/* Instrucciones para iPhone */}
            {isIOS && (
              <Card className="p-4 sm:p-6 space-y-4 sm:space-y-5 border-2 border-blue-500/20 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="flex items-center gap-2 sm:gap-3 animate-in fade-in slide-in-from-left-4 duration-500 delay-150">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Share2 className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
                  </div>
                  <div>
                    <h2 className="font-bold text-lg sm:text-xl">
                      {language === "es" ? "📱 iPhone / iPad" : "📱 iPhone / iPad"}
                    </h2>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {language === "es"
                        ? "Usa Safari - otros navegadores no funcionan"
                        : "Use Safari - other browsers won't work"}
                    </p>
                  </div>
                </div>

                <div className="space-y-3 sm:space-y-4">
                  {[
                    {
                      step: "1",
                      icon: <Share2 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />,
                      title: language === "es" ? "Abre el menú de Compartir" : "Open Share menu",
                      desc: language === "es" 
                        ? "Toca el icono de compartir (cuadrado con flecha ↑) en la parte inferior de Safari"
                        : "Tap the share icon (square with arrow ↑) at the bottom of Safari"
                    },
                    {
                      step: "2",
                      icon: <Smartphone className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />,
                      title: language === "es" ? "Busca 'Añadir a Inicio'" : "Find 'Add to Home Screen'",
                      desc: language === "es"
                        ? "Desplázate hacia abajo en el menú y busca esta opción"
                        : "Scroll down in the menu and look for this option"
                    },
                    {
                      step: "3",
                      icon: <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />,
                      title: language === "es" ? "Confirma la instalación" : "Confirm installation",
                      desc: language === "es"
                        ? "Toca 'Añadir' arriba a la derecha. ¡Listo!"
                        : "Tap 'Add' in the top right. Done!"
                    }
                  ].map((item, idx) => (
                    <div 
                      key={idx} 
                      className="flex gap-2 sm:gap-4 p-3 sm:p-4 bg-blue-500/5 rounded-lg border border-blue-500/10 animate-in fade-in slide-in-from-right-4 duration-500"
                      style={{ animationDelay: `${300 + idx * 150}ms` }}
                    >
                      <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-lg">
                        {item.step}
                      </div>
                      <div className="flex-1 space-y-0.5 sm:space-y-1">
                        <div className="flex items-center gap-1.5 sm:gap-2 mb-1">
                          {item.icon}
                          <h3 className="font-semibold text-sm sm:text-base">{item.title}</h3>
                        </div>
                        <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Instrucciones para Android */}
            {isAndroid && !deferredPrompt && (
              <Card className="p-4 sm:p-6 space-y-4 sm:space-y-5 border-2 border-green-500/20 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="flex items-center gap-2 sm:gap-3 animate-in fade-in slide-in-from-left-4 duration-500 delay-150">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Chrome className="w-5 h-5 sm:w-6 sm:h-6 text-green-500" />
                  </div>
                  <div>
                    <h2 className="font-bold text-lg sm:text-xl">
                      {language === "es" ? "🤖 Android" : "🤖 Android"}
                    </h2>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {language === "es"
                        ? "Funciona en Chrome y la mayoría de navegadores"
                        : "Works in Chrome and most browsers"}
                    </p>
                  </div>
                </div>

                <div className="space-y-3 sm:space-y-4">
                  {[
                    {
                      step: "1",
                      icon: <MoreVertical className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />,
                      title: language === "es" ? "Abre el menú" : "Open menu",
                      desc: language === "es"
                        ? "Toca los tres puntos (⋮) en la esquina superior derecha"
                        : "Tap the three dots (⋮) in the top right corner"
                    },
                    {
                      step: "2",
                      icon: <Download className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />,
                      title: language === "es" ? "Busca 'Instalar aplicación'" : "Look for 'Install app'",
                      desc: language === "es"
                        ? "También puede decir 'Añadir a pantalla de inicio'"
                        : "It may also say 'Add to Home screen'"
                    },
                    {
                      step: "3",
                      icon: <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />,
                      title: language === "es" ? "Confirma" : "Confirm",
                      desc: language === "es"
                        ? "Toca 'Instalar' o 'Añadir'. ¡Listo!"
                        : "Tap 'Install' or 'Add'. Done!"
                    }
                  ].map((item, idx) => (
                    <div 
                      key={idx} 
                      className="flex gap-2 sm:gap-4 p-3 sm:p-4 bg-green-500/5 rounded-lg border border-green-500/10 animate-in fade-in slide-in-from-right-4 duration-500"
                      style={{ animationDelay: `${300 + idx * 150}ms` }}
                    >
                      <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-lg">
                        {item.step}
                      </div>
                      <div className="flex-1 space-y-0.5 sm:space-y-1">
                        <div className="flex items-center gap-1.5 sm:gap-2 mb-1">
                          {item.icon}
                          <h3 className="font-semibold text-sm sm:text-base">{item.title}</h3>
                        </div>
                        <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Beneficios */}
            <Card className="p-6 space-y-4 bg-primary/5">
              <h3 className="font-bold text-lg">
                {language === "es" ? "✨ Beneficios de instalar" : "✨ Installation benefits"}
              </h3>
              <ul className="space-y-3">
                {[
                  language === "es" ? "🚀 Acceso instantáneo desde tu pantalla de inicio" : "🚀 Instant access from your home screen",
                  language === "es" ? "📴 Funciona sin conexión a internet" : "📴 Works offline without internet",
                  language === "es" ? "🔔 Recibe notificaciones importantes" : "🔔 Receive important notifications",
                  language === "es" ? "⚡ Carga más rápida que el navegador" : "⚡ Loads faster than browser",
                  language === "es" ? "💾 Ahorra datos móviles" : "💾 Saves mobile data"
                ].map((benefit, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}